import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { fmtDateTime } from "./util";
import type { ActivityKind, CrmCatalog, LeadActivity } from "../types";

/**
 * Лента касаний по клиенту: звонки, письма, встречи, заметки.
 *
 * Карточка отвечает на вопрос «кто это и что ему нужно», лента — на вопрос
 * «что с ним уже происходило». Отметки сервиса (смена статуса, готовое письмо)
 * попадают сюда сами.
 */

const FALLBACK: ActivityKind[] = [
  { key: "call", label: "Звонок", icon: "📞" },
  { key: "whatsapp", label: "WhatsApp", icon: "💬" },
  { key: "email", label: "Письмо", icon: "✉️" },
  { key: "meeting", label: "Встреча", icon: "🤝" },
  { key: "note", label: "Заметка", icon: "📝" },
  { key: "system", label: "Сервис", icon: "•" },
];

export function LeadTimeline({
  meetingId,
  catalog,
}: {
  meetingId: number;
  catalog: CrmCatalog | null;
}) {
  const [items, setItems] = useState<LeadActivity[]>([]);
  const [kind, setKind] = useState("call");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const kinds = catalog?.activity_kinds ?? FALLBACK;
  const manual = kinds.filter((k) => k.key !== "system");
  const kindOf = (key: string) => kinds.find((k) => k.key === key);

  const load = useCallback(() => {
    api
      .leadActivities(meetingId)
      .then(setItems)
      .catch((e) => setError(e.message));
  }, [meetingId]);

  useEffect(load, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError("");
    try {
      const created = await api.addLeadActivity(meetingId, kind, text.trim());
      setItems((prev) => [created, ...prev]);
      setText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm("Удалить запись из истории?")) return;
    try {
      await api.deleteLeadActivity(meetingId, id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <h3>История работы с клиентом</h3>

      {error && <div className="banner red">{error}</div>}

      <form onSubmit={submit} className="touch-form">
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          {manual.map((k) => (
            <option key={k.key} value={k.key}>
              {k.icon} {k.label}
            </option>
          ))}
        </select>
        <input
          placeholder="Что произошло: дозвонился, договорились созвониться в среду"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={4000}
        />
        <button className="primary" disabled={busy || !text.trim()}>
          {busy ? "…" : "Записать"}
        </button>
      </form>

      {items.length === 0 ? (
        <p className="muted small">
          Пока пусто. Записывайте сюда каждый звонок и письмо — через месяц это
          единственное, что ответит на вопрос «на чём мы остановились».
        </p>
      ) : (
        <ul className="timeline">
          {items.map((a) => {
            const k = kindOf(a.kind);
            const system = a.kind === "system";
            return (
              <li key={a.id}>
                <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
                  <span>{k?.icon ?? "•"}</span>
                  <span style={{ flex: 1 }} className={system ? "muted" : undefined}>
                    {a.text}
                  </span>
                  {!system && (
                    <button className="sm ghost" onClick={() => remove(a.id)}>
                      Удалить
                    </button>
                  )}
                </div>
                <div className="when">
                  {fmtDateTime(a.created_at)}
                  {a.user_name ? ` · ${a.user_name}` : ""}
                  {/* «звонок», «письмо» — со строчной; названия вроде WhatsApp — как есть */}
                  {!system && k
                    ? ` · ${/^[А-ЯЁ]/.test(k.label) ? k.label.toLowerCase() : k.label}`
                    : ""}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
